import React from 'react';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import styles from './loader.css';
import PropTypes from 'prop-types';

import topBlock from './top-block.svg';
import middleBlock from './middle-block.svg';
import bottomBlock from './bottom-block.svg';

const messages = [
    {
        message: (
            <FormattedMessage
                defaultMessage="正在创建积木…"
                description="One of the loading messages"
                id="gui.loader.message1"
            />
        ),
        weight: 50
    },
    {
        message: (
            <FormattedMessage
                defaultMessage="正在加载角色…"
                description="One of the loading messages"
                id="gui.loader.message2"
            />
        ),
        weight: 50
    },
    {
        message: (
            <FormattedMessage
                defaultMessage="正在加载声音…"
                description="One of the loading messages"
                id="gui.loader.message3"
            />
        ),
        weight: 50
    },
    {
        message: (
            <FormattedMessage
                defaultMessage="正在加载扩展…"
                description="One of the loading messages"
                id="gui.loader.message4"
            />
        ),
        weight: 50
    },
    {
        message: (
            <FormattedMessage
                defaultMessage="正在创建积木…"
                description="One of the loading messages"
                id="gui.loader.message1"
            />
        ),
        weight: 20
    },
    {
        message: (
            <FormattedMessage
                defaultMessage="正在召集小猫…"
                description="One of the loading messages"
                id="gui.loader.message5"
            />
        ),
        weight: 1
    },
    {
        message: (
            <FormattedMessage
                defaultMessage="正在传输纳米信号…"
                description="One of the loading messages"
                id="gui.loader.message6"
            />
        ),
        weight: 1
    },
    {
        message: (
            <FormattedMessage
                defaultMessage="正在给投影片充气…"
                description="One of the loading messages"
                id="gui.loader.message7"
            />
        ),
        weight: 1
    },
    {
        message: (
            <FormattedMessage
                defaultMessage="正在准备表情素材…"
                description="One of the loading messages"
                id="gui.loader.message8"
            />
        ),
        weight: 1
    }
];

const mainMessages = {
    'gui.loader.headline': (
        <FormattedMessage
            defaultMessage="正在加载项目"
            description="Main loading message"
            id="gui.loader.headline"
        />
    ),
    'gui.loader.creating': (
        <FormattedMessage
            defaultMessage="正在创建项目"
            description="Main creating message"
            id="gui.loader.creating"
        />
    )
};

const defaultProgressState = {
    progress: null,
    sizeText: '',
    statusText: '',
    stageText: ''
};

const getExternalLoaderState = () => {
    if (typeof window === 'undefined') return defaultProgressState;
    return {
        ...defaultProgressState,
        ...(window.__scratchGuiLoaderState || {})
    };
};

class LoaderComponent extends React.Component {
    constructor (props) {
        super(props);
        this.handleExternalProgress = this.handleExternalProgress.bind(this);
        this.state = {
            messageNumber: this.chooseRandomMessage(),
            ...getExternalLoaderState()
        };
    }
    componentDidMount () {
        this.intervalId = setInterval(() => {
            this.setState({messageNumber: this.chooseRandomMessage()});
        }, 5000);

        if (typeof window !== 'undefined') {
            window.addEventListener('scratch-loader-progress', this.handleExternalProgress);
            this.handleExternalProgress();
        }
    }
    componentWillUnmount () {
        clearInterval(this.intervalId);
        if (typeof window !== 'undefined') {
            window.removeEventListener('scratch-loader-progress', this.handleExternalProgress);
        }
    }
    handleExternalProgress (event) {
        const nextState = event && event.detail ? event.detail : getExternalLoaderState();
        this.setState({
            progress: typeof nextState.progress === 'number' ? nextState.progress : null,
            sizeText: nextState.sizeText || '',
            statusText: nextState.statusText || '',
            stageText: nextState.stageText || ''
        });
    }
    chooseRandomMessage () {
        let messageNumber;
        const sum = messages.reduce((acc, m) => acc + m.weight, 0);
        let rand = sum * Math.random();
        for (let i = 0; i < messages.length; i++) {
            rand -= messages[i].weight;
            if (rand <= 0) {
                messageNumber = i;
                break;
            }
        }
        return messageNumber;
    }
    render () {
        const hasProgress = typeof this.state.progress === 'number';
        const progressValue = hasProgress ? Math.max(0, Math.min(100, Math.round(this.state.progress))) : 0;

        return (
            <div
                className={classNames(styles.background, {
                    [styles.fullscreen]: this.props.isFullScreen
                })}
            >
                <div className={styles.container}>
                    <div className={styles.blockAnimation}>
                        <img
                            className={styles.topBlock}
                            src={topBlock}
                        />
                        <img
                            className={styles.middleBlock}
                            src={middleBlock}
                        />
                        <img
                            className={styles.bottomBlock}
                            src={bottomBlock}
                        />
                    </div>
                    <div className={styles.title}>
                        {mainMessages[this.props.messageId]}
                    </div>
                    <div className={styles.messageContainerOuter}>
                        <div
                            className={styles.messageContainerInner}
                            style={{transform: `translate(0, -${this.state.messageNumber * 25}px)`}}
                        >
                            {messages.map((m, i) => (
                                <div
                                    className={styles.message}
                                    key={i}
                                >
                                    {m.message}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={styles.progressShell}>
                        <div
                            className={styles.progressTrack}
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={progressValue}
                        >
                            <div
                                className={classNames(styles.progressBar, {
                                    [styles.progressBarIndeterminate]: !hasProgress
                                })}
                                style={hasProgress ? {width: `${progressValue}%`} : undefined}
                            />
                        </div>
                        <div className={styles.progressMeta}>
                            <span>{hasProgress ? `${progressValue}%` : '…'}</span>
                            <span>{this.state.sizeText || '正在准备 Scratch 编辑器资源…'}</span>
                        </div>
                        <div className={styles.statusLine}>
                            {this.state.statusText || '正在加载 Scratch 编辑器资源…'}
                        </div>
                        <div className={styles.stageHint}>
                            {this.state.stageText || '编辑器启动时，这里会显示分包加载进度。'}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

LoaderComponent.propTypes = {
    isFullScreen: PropTypes.bool,
    messageId: PropTypes.string
};
LoaderComponent.defaultProps = {
    isFullScreen: false,
    messageId: 'gui.loader.headline'
};

export default LoaderComponent;
